import { get, del, post, put } from "./apiHelper";

import type {
  GetUserResponse,
  GetMyProfileResponse,
  AgreeToProfileTermsRequest,
  SaveProfileBillingRequest,
  SaveProfileShippingRequest,
  SaveProfileContactRequest,
  SaveProfileCredentialsRequest,
  SaveProfileExperienceRequest,
  SaveProfileAreaRequest,
  SaveProfileShippingAreaRequest,
  SaveOutOfOfficeRequest,
  AcceptNotaryBidRequest,
  DeclineNotaryBidRequest,
  ReadManyNotificationsRequest,
  UnreadManyNotificationsRequest,
  DeleteManyNotificationsRequest,
  CreateMessageRequest,
  AddOrderDocRequest,
  SetAppointmentRequest,
  ConfirmSigningRequest,
  UploadOrderDocPageRequest,
  UploadOrderDocPdfRequest,
  CreateNotaryGlbaRequest,
  UpdateGeneralRequest,
  UpdateSecurityRequest,
  UpdateAccessRequest,
  UploadCredentialRequest,
  GetNotificationDtResponse,
  GetNotificationListResponse,
  GetOrdersInvoicesDtResponse,
  GetLanguagesResponse,
} from "~/types/backend";

import { flattenFilters } from "./utilHelper";
import { TListParams } from "~/types/global";

const getEntityList = <T = any>(url: string, params: TListParams) => get<T>(url, { params: flattenFilters(params) });

// User

export const getAuthUser = () => get<GetUserResponse>('/me');

export const getMyProfile = () => get<GetMyProfileResponse>('/me/notary-profile');

export const goIdle = () => put('/me/idle');

export const goActive = () => put('/me/active');

// Profile

export const agreeToProfileTerms = (data: AgreeToProfileTermsRequest) => put('/notary/profile/terms', data);

export const saveMyProfileBilling = (data: SaveProfileBillingRequest) => put('/notary/profile/billing', data);

export const saveMyProfileShipping = (data: SaveProfileShippingRequest) => put('/notary/profile/shipping', data);

export const saveMyProfileContact = (data: SaveProfileContactRequest) => put('/notary/profile/contact', data);

export const saveMyProfileCredentials = (data: SaveProfileCredentialsRequest) => put('/notary/profile/credentials', data);

export const saveMyProfileExperience = (data: SaveProfileExperienceRequest) => put('/notary/profile/experience', data);

export const saveMyProfileArea = (data: SaveProfileAreaRequest) => put('/notary/profile/area', data);

export const saveMyProfileShippingArea = (data: SaveProfileShippingAreaRequest) => put('/notary/profile/shipping-area', data);

export const acceptNotaryPromotionToSen = () => post('/notary/accept-promotion/sen');

export const declineNotaryPromotionToSen = () => post('/notary/decline-promotion/sen');

// Out Of Office

export const saveOutOfOffice = (id: number, data: SaveOutOfOfficeRequest) => post(`/user/${id}/out-of-office`, data);

export const getOutOfOffice = (id: number) => get(`/user/${id}/out-of-office`);


// Credential Doc

export const getCredentials = (id: number) => get(`/notary/${id}/credentials`);

export const uploadCredential = (data: UploadCredentialRequest) => post(`/notary/profile/credential`, data);

export const deleteCredential = (id: number) => del(`/notary-credential/${id}`);

// Notary Bids

export const getMyNotaryBids = (params: TListParams) => getEntityList('/notary-bids/my', params);

export const getNotaryBid = (id: number) => get(`/notary-bid/${id}`);

export const acceptNotaryBid = (id: number, data: AcceptNotaryBidRequest) => put(`/notary-bid/${id}/accept`, data);

export const declineNotaryBid = (id: number, data: DeclineNotaryBidRequest) => put(`/notary-bid/${id}/decline`, data);

// Notification

export const getNotificationDt = (params: TListParams) => getEntityList<GetNotificationDtResponse>('/notifications', params);

export const readManyNotifications = (data: ReadManyNotificationsRequest) => put('/notifications/read', data);

export const unreadManyNotifications = (data: UnreadManyNotificationsRequest) => put('/notifications/unread', data);

export const deleteManyNotifications = (data: DeleteManyNotificationsRequest) => del('/notifications', { data });

export const getNotificationList = () => getEntityList<GetNotificationListResponse>('/notifications', { page: 1, pageSize: 10, sortBy: 'id', sortDir: 'desc' });

export const getNotification = (id: number) => get(`/notification/${id}`);

export const deleteNotification = (id: number) => del(`/notification/${id}`);

export const readNotification = (id: number) => put(`/notification/${id}/read`);

export const unreadNotification = (id: number) => put(`/notification/${id}/unread`);

// Invoices

export const getOrdersInvoicesDt = (params: TListParams) => getEntityList<GetOrdersInvoicesDtResponse>('/orders/invoices/notary', params);

// Messages

export const getMessages = (params: TListParams) => getEntityList('/messages', params);

export const createMessage = (data: CreateMessageRequest, config?: any) => post('/message', data, config);

export const setChannelLastSeen = (orderId: number, channelId: number) => post(`/order/${orderId}/messages/${channelId}/last-seen`);


// Dashboard
export const getStatistics = (unit: string) => get(`/dashboard/notary/leads-stats/${unit}`);

export const getChats = () => get('/dashboard/notary/recent-chats');

export const getInProgressOrders = (params: number) => get(`/dashboard/notary/in-progress-orders?limit=${params}`);

// Orders

export const getOrders = (params: TListParams) => getEntityList('/notary/my-orders', params);

export const getOrder = (id: number) => get(`/notary/order/${id}`);

export const addOrderDoc = (data: AddOrderDocRequest, orderId: number) => post(`/order/${orderId}/doc/add`, data);

export const getOrderMessages = (orderId: number, channelId: number, params: TListParams) => getEntityList(`/order/${orderId}/messages/${channelId}`, params);

export const getAllOrderMessages = (orderId: number, params: TListParams) => getEntityList(`/order/${orderId}/messages`, params);

export const getChatMembers = (orderId: number, channelId: number) => get(`/order/${orderId}/messages/${channelId}/participants`);

export const createOrderMeeting = (orderId: number, role: string) => post(`/order/${orderId}/meet/${role}`);

// OrderDoc

export const deleteOrderDoc = (id: number) => del(`/order-doc/${id}`);

export const deleteAdditionalDocs = (id: number) => del(`/notary/order/${id}/additional-notary-docs`);

export const uploadOrderDocPage = (data: UploadOrderDocPageRequest, id: number, page: number, config?: any) => post(`/order-doc/${id}/page/${page}`, data, config);

export const clearOrderDocPage = (id: number, page: number) => put(`/order-doc/${id}/page/${page}/clear`);

export const getInkSignOrderDocs = (orderId: number) => get(`/order/${orderId}/docs/ink-sign`);

export const uploadOrderDocPdf = (id: number, data: UploadOrderDocPdfRequest) => put(`/order-doc/${id}/pdf/upload`, data);

export const deleteOrderDocPdf = (id: number) => del(`/order-doc/${id}/pdf/delete`);

// Order Notary

export const docsReceived = (id: number) => put(`/notary/order/${id}/docs-received`);

export const setAppointment = (id: number, data: SetAppointmentRequest) => put(`/notary/order/${id}/set-appointment`, data);

export const confirmAppointment = (id: number) => put(`/notary/order/${id}/confirm-appointment`);

export const prepareSigning = (id: number) => put(`/notary/order/${id}/docs-printed`);

export const confirmSigning = (id: number, data: ConfirmSigningRequest) => put(`/notary/order/${id}/docs-uploaded`, data);

export const returnDocToDealer = (id: number, orderDocId: number) => put(`/notary/order/${id}/doc/${orderDocId}/sent`);

export const returnAllDocsToDealer = (id: number) => put(`/notary/order/${id}/docs-sent`);

export const completeOrder = (id: number) => put(`/notary/order/${id}/complete-order`);

export const printDocuments = (id: number, config?: any) => get(`/order/${id}/docs/print-all`, config);

// Fedex and UPS

export const getOrderShipping = (id: number) => get(`/order/${id}/track/shipping`);

// Order Fees

export const getOrderFees = (id: number) => get(`/order/${id}/fees`);

// Knowledgebase

export const getServicedeskArticles = (searchTerm: string) => get(`/knowledgebase/articles?query=${searchTerm}`);

// Notary GLBA
export const createNotaryGlba = (id: number, data: CreateNotaryGlbaRequest) => post(`/notary/${id}/glba`, data);

export const updateGeneral = (id: number, data: UpdateGeneralRequest) => put(`/notary-glba/${id}/general`, data);

export const updateSecurity = (id: number, data: UpdateSecurityRequest) => put(`/notary-glba/${id}/security`, data);

export const updateAccess = (id: number, data: UpdateAccessRequest) => put(`/notary-glba/${id}/access`, data);

export const getNotaryGlba = (id: number) => get(`/notary-glba/${id}`);

// Message template

export const getMessageTemplatesChannel = (channelId: number) => get(`/message-templates/${channelId}`);

// Language

export const getLanguages = () => get<GetLanguagesResponse>('/languages');